import React, { useState, useEffect, useCallback } from 'react';
import apiService from '../../services/api';
import '../../styles/PractitionerScheduleEditor.css';

const DAYS = [
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
  { value: 0, label: 'Sunday' },
];

const EMPTY_BLOCK = (isBreak = false) => ({
  startTime: '09:00:00',
  endTime:   '17:00:00',
  isBreak,
  locationId:            null,
  maxConcurrentBookings: 1,
  notes:                 '',
});

const formatTime = (ts) => {
  if (!ts) return '—';
  const parts = ts.toString().split(':');
  const h = parseInt(parts[0], 10);
  const m = parts[1] || '00';
  const ampm = h >= 12 ? 'pm' : 'am';
  return `${h % 12 || 12}:${m}${ampm}`;
};

const toTimeInput = (ts) => {
  if (!ts) return '09:00';
  const parts = ts.toString().split(':');
  return `${parts[0].padStart(2, '0')}:${(parts[1] || '00').padStart(2, '0')}`;
};

const fromTimeInput = (val) => `${val}:00`;

// ── BlockRow component ────────────────────────────────────────────────────────
const BlockRow = ({ block, locations, onChange, onRemove, index }) => {
  const locationOptions = locations.filter(l => !l.isDeleted);

  return (
    <div className={`sched-block-row ${block.isBreak ? 'sched-block-break' : 'sched-block-work'}`}>
      <div className="sched-block-type-badge">
        {block.isBreak ? '⏸ Break' : '✅ Work'}
      </div>

      <div className="sched-block-fields">
        {/* Start / End time */}
        <label className="sched-field">
          <span>Start</span>
          <input
            type="time"
            value={toTimeInput(block.startTime)}
            onChange={e => onChange(index, 'startTime', fromTimeInput(e.target.value))}
          />
        </label>

        <label className="sched-field">
          <span>End</span>
          <input
            type="time"
            value={toTimeInput(block.endTime)}
            onChange={e => onChange(index, 'endTime', fromTimeInput(e.target.value))}
          />
        </label>

        {/* Location — only shown for Work blocks */}
        {!block.isBreak && (
          <label className="sched-field sched-field-wide">
            <span>Location</span>
            <select
              value={block.locationId || ''}
              onChange={e => onChange(index, 'locationId', e.target.value || null)}
            >
              <option value="">— Any / not specified —</option>
              {locationOptions.map(loc => (
                <option key={loc.id} value={loc.id}>
                  {loc.locationType === 'Online' || loc.locationType === 1
                    ? `💻 ${loc.name}`
                    : `🏥 ${loc.name}`}
                </option>
              ))}
            </select>
          </label>
        )}

        {/* Notes */}
        <label className="sched-field sched-field-wide">
          <span>Notes</span>
          <input
            type="text"
            placeholder={block.isBreak ? 'e.g. Lunch break' : 'e.g. Clinic A mornings'}
            maxLength={200}
            value={block.notes || ''}
            onChange={e => onChange(index, 'notes', e.target.value)}
          />
        </label>
      </div>

      {/* Toggle break / work */}
      <button
        type="button"
        className="sched-btn sched-btn-toggle"
        title={block.isBreak ? 'Convert to work block' : 'Convert to break'}
        onClick={() => onChange(index, 'isBreak', !block.isBreak)}
      >
        {block.isBreak ? '▶ Make work' : '⏸ Make break'}
      </button>

      <button
        type="button"
        className="sched-btn sched-btn-remove"
        title="Remove block"
        onClick={() => onRemove(index)}
      >
        ✕
      </button>
    </div>
  );
};

// ── DayPanel component ────────────────────────────────────────────────────────
const DayPanel = ({ day, blocks: initialBlocks, locations, onSave, saving }) => {
  const [blocks, setBlocks] = useState(initialBlocks || []);
  const [dirty,  setDirty]  = useState(false);

  useEffect(() => {
    setBlocks(initialBlocks || []);
    setDirty(false);
  }, [initialBlocks]);

  const markDirty = (newBlocks) => {
    setBlocks(newBlocks);
    setDirty(true);
  };

  const handleChange = (idx, field, value) => {
    const next = blocks.map((b, i) => i === idx ? { ...b, [field]: value } : b);
    markDirty(next);
  };

  const handleAdd = (isBreak = false) => {
    const last = blocks[blocks.length - 1];
    const startTime = last ? last.endTime : '09:00:00';
    const endTime   = last
      ? (() => {
          const [h, m] = last.endTime.split(':');
          const next = new Date(0, 0, 0, parseInt(h, 10) + (isBreak ? 1 : 2), parseInt(m, 10));
          return `${String(next.getHours()).padStart(2,'0')}:${String(next.getMinutes()).padStart(2,'0')}:00`;
        })()
      : isBreak ? '10:00:00' : '17:00:00';

    markDirty([...blocks, { ...EMPTY_BLOCK(isBreak), startTime, endTime }]);
  };

  const handleRemove = (idx) => {
    markDirty(blocks.filter((_, i) => i !== idx));
  };

  const handleSave = () => {
    onSave(day.value, blocks.map((b, idx) => ({
      startTime:             b.startTime,
      endTime:               b.endTime,
      isBreak:               b.isBreak,
      locationId:            b.isBreak ? null : (b.locationId || null),
      maxConcurrentBookings: b.maxConcurrentBookings || 1,
      sortOrder:             idx,
      notes:                 b.notes || null,
    })));
    setDirty(false);
  };

  const hasWork = blocks.some(b => !b.isBreak);
  const isOff   = blocks.length === 0;

  return (
    <div className="sched-day-panel">
      <div className="sched-day-header">
        <span className={`sched-day-status ${isOff ? 'status-off' : 'status-active'}`}>
          {isOff ? '🚫 Day off' : `${blocks.length} block${blocks.length !== 1 ? 's' : ''}`}
        </span>
        {hasWork && (
          <span className="sched-day-summary">
            {formatTime(blocks.find(b => !b.isBreak)?.startTime)} –{' '}
            {formatTime([...blocks].reverse().find(b => !b.isBreak)?.endTime)}
          </span>
        )}
      </div>

      <div className="sched-blocks-list">
        {blocks.length === 0 && (
          <p className="sched-empty-msg">No schedule set — practitioner is off this day.</p>
        )}
        {blocks.map((block, idx) => (
          <BlockRow
            key={idx}
            index={idx}
            block={block}
            locations={locations}
            onChange={handleChange}
            onRemove={handleRemove}
          />
        ))}
      </div>

      <div className="sched-day-actions">
        <button
          type="button"
          className="sched-btn sched-btn-add-work"
          onClick={() => handleAdd(false)}
        >
          + Work block
        </button>
        <button
          type="button"
          className="sched-btn sched-btn-add-break"
          onClick={() => handleAdd(true)}
        >
          + Break
        </button>

        {dirty && (
          <button
            type="button"
            className="sched-btn sched-btn-save"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving…' : '💾 Save this day'}
          </button>
        )}
      </div>
    </div>
  );
};

// ── Main export ───────────────────────────────────────────────────────────────
const PractitionerScheduleEditor = ({ practitionerId, showNotification }) => {
  const [activeDay,    setActiveDay]    = useState(1); // default Monday
  const [schedule,     setSchedule]     = useState(null);
  const [locations,    setLocations]    = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [saving,       setSaving]       = useState(false);
  const [error,        setError]        = useState(null);

  const loadData = useCallback(async () => {
    if (!practitionerId) return;
    setLoading(true);
    setError(null);
    try {
      const [sched, locs] = await Promise.all([
        apiService.getWeeklySchedule(practitionerId),
        apiService.getOffices(),
      ]);
      setSchedule(sched);
      setLocations(Array.isArray(locs) ? locs : (locs?.items || []));
    } catch (err) {
      setError('Failed to load schedule. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [practitionerId]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSaveDay = async (dayOfWeek, blocks) => {
    setSaving(true);
    try {
      const result = await apiService.putDaySchedule(practitionerId, dayOfWeek, { blocks });
      // Update local state with returned blocks
      setSchedule(prev => ({
        ...prev,
        days: prev.days.map(d =>
          d.dayOfWeek === dayOfWeek
            ? { ...d, blocks: result.blocks }
            : d
        ),
      }));
      showNotification?.('Schedule saved', 'success');
    } catch (err) {
      showNotification?.('Failed to save schedule', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return (
    <div className="sched-loading">
      <div className="sched-spinner" />
      Loading schedule…
    </div>
  );

  if (error) return (
    <div className="sched-error">
      <p>{error}</p>
      <button className="sched-btn sched-btn-save" onClick={loadData}>Retry</button>
    </div>
  );

  const activeDay_ = DAYS.find(d => d.value === activeDay);
  const dayData    = schedule?.days?.find(d => d.dayOfWeek === activeDay);

  return (
    <div className="sched-editor">
      {/* Day tabs */}
      <div className="sched-tabs" role="tablist">
        {DAYS.map(day => {
          const d   = schedule?.days?.find(x => x.dayOfWeek === day.value);
          const hasWork = d?.blocks?.some(b => !b.isBreak);
          return (
            <button
              key={day.value}
              role="tab"
              aria-selected={activeDay === day.value}
              className={`sched-tab ${activeDay === day.value ? 'active' : ''} ${hasWork ? 'has-schedule' : ''}`}
              onClick={() => setActiveDay(day.value)}
            >
              <span className="sched-tab-label">{day.label.slice(0, 3)}</span>
              {hasWork && <span className="sched-tab-dot" />}
            </button>
          );
        })}
      </div>

      {/* Active day editor */}
      <div className="sched-day-content" role="tabpanel">
        <h4 className="sched-day-title">{activeDay_?.label}</h4>
        <DayPanel
          key={activeDay}
          day={activeDay_}
          blocks={dayData?.blocks?.map(b => ({
            startTime:             b.startTime,
            endTime:               b.endTime,
            isBreak:               b.isBreak,
            locationId:            b.locationId || null,
            maxConcurrentBookings: b.maxConcurrentBookings || 1,
            notes:                 b.notes || '',
          })) || []}
          locations={locations}
          onSave={handleSaveDay}
          saving={saving}
        />
      </div>
    </div>
  );
};

export default PractitionerScheduleEditor;
